import { type DocNode } from '@atlaskit/adf-schema';
import { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import type {
	AiFeatureRequestEnvelopeRovoConvoStarterRecommendationRequestNew,
	AIFeatureResponseEnvelopeRovoConvoStarterRecommendationResponseNew,
} from '@atlassian/convo-ai-api';

import { ASSISTANCE_SERVICE_ERROR_CODES } from '../../common/constants/public';
import { isApiError } from '../../common/utils/api-error';

import { type AgentAction } from './agent-actions/types';
import {
	type MessageAction,
	type MessageActionPayload,
	type MessageActionPayloadContext,
	type MessageActionResponse,
} from './message-actions/types';
import { type PluginInvocationMessage } from './plugins';

/**
 * Represents a parsed SSE message with metadata from the standard W3C SSE format.
 * When includeMetadata is true, readSSEStream yields this type.
 * - eventType: from SSE `event:` line
 * - id: from SSE `id:` line (used e.g. for Last-Event-ID / reconnect).
 * - data: parsed JSON from SSE `data:` line.
 * The __isSSEWrapper discriminator ensures this type is never confused with StreamMessage.
 */
export type ParsedSSEMessage<T> = {
	/** Discriminator field - guarantees this is an SSE wrapper, not a StreamMessage */
	__isSSEWrapper: true;
	eventType?: string;
	data: T;
	id?: string;
};

/**
 * Type guard to check if a message is a ParsedSSEMessage wrapper.
 * Uses the __isSSEWrapper discriminator to avoid false positives with StreamMessage.
 */
export const isSSEMessage = <T>(message: unknown): message is ParsedSSEMessage<T> => {
	return (
		message !== null &&
		typeof message === 'object' &&
		'__isSSEWrapper' in message &&
		(message as ParsedSSEMessage<T>).__isSSEWrapper === true
	);
};

export const CONVERSATION_CHANNEL_NOT_FOUND_ERROR = 'convoai.rest.not.found' as const;

export const ACCEPTABLE_USE_VIOLATIONS_ERROR = 'ACCEPTABLE_USE_VIOLATIONS' as const;

export type MessageFileId = {
	id: string;
	name: string;
};

export type WebSearchConfigurationResponse = {
	resourceAri: string;
	enabled: boolean;
};

export enum PromptCategory {
	ANALYZE = 'Analyze',
	BRAINSTORM = 'Brainstorm',
	REVIEW = 'Review',
	SUMMARIZE = 'Summarize',
	CREATE = 'Create',
	EXECUTE = 'Execute',
	FIND = 'Find',
	LEARN = 'Learn',
}

export enum PromptRole {
	GENERAL = 'General',
	ENGINEERING = 'Engineering',
	PRODUCT = 'Product',
	PROGRAM_MANAGEMENT = 'Program Management',
	DESIGN = 'Design',
	MARKETING = 'Marketing',
	IT_OPS = 'IT Ops',
	SALES = 'Sales',
	CUSTOMER_SUPPORT = 'Customer Support',
	HELPDESK_SUPPORT = 'Helpdesk Support',
	SECURITY = 'Security',
}

export type SimpleUserProfile = {
	id: string;
	ari: string;
	name: string;
};

export type GetPromptResponse = {
	id: string;
	prompt: string;
	title: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	creator: SimpleUserProfile;
	sharedWith?: SimpleUserProfile[];
	isFavorited: boolean;
	appIds?: string[];
};

export type GetAllPromptsResponse = {
	prompts: GetPromptResponse[];
};

export type CreatePromptRequest = {
	title: string;
	prompt: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	appIds?: string[];
};

export type ConversationContent = {
	id: string;
	content_display_name: string;
	content_ari: string;
	created_at: string;
	action_conversation_id: string;
	invocation_id: string;
	action_status: string;
	action_key: string;
	owner: string;
};

export type HumanMessage = {
	role: 'HUMAN';
	content: string | DocNode;
	id: string;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	isFromSeededConversation?: boolean;
	requestId?: string;
	file_uploads?: MessageFileId[];
};

export type AssistantMessage = Message & {
	role: 'ASSISTANT';
};

// ************************************************************************************************
// Response data & metadata
// Sent by POST:/chat endpoint
// ************************************************************************************************

export type ModelUsage = {
	total_tokens: number;
	prompt_tokens: number;
	completion_tokens: number;
	request_count: number;
	total_cost: number;
	duration: number;
};

export type Usage = {
	model_usage: {
		[key: string]: ModelUsage;
	};
	total: ModelUsage;
};

export type Durations = {
	events: {
		[key: string]: number;
	};
	total: number;
};

export type DocSegment = {
	content: string;
	token_count: number;
	length: number;
	parent_ari: string;
	similarity_score: number;
};

export type DocSegments = Array<DocSegment>;

export type AgentResponse = {
	message: AssistantMessage;
	human_message?: HumanMessage;
	usage_data?: Usage;
	durations_data?: Durations;
};

// ************************************************************************************************
//  Streaming Messages
// Sent by POST:/chat/stream endpoint
// ************************************************************************************************

export type StreamResponse = {
	type: 'FINAL_RESPONSE';
	message: AgentResponse;
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
	streamingComplete?: boolean;
};

export type StreamError = {
	type: 'ERROR';
	message: {
		content: string;
		status_code?: number;
		message_template?: string;
	};
	millisOffset: number;
	metadata: null | {
		error_message?: string;
		request_id?: string;
		timeout?: number;
	};
};

export type StreamReconnectSupportPart = {
	type: 'RECONNECT_SUPPORTED';
};

export type StreamHeartbeat = {
	type: 'HEART_BEAT';
	content: string;
};

export type StreamAnswerPart = {
	type: 'ANSWER_PART';
	message: { content: string; role: 'ASSISTANT' };
	millisOffset: number;
	metadata?: {
		run_id?: string;
		request_id?: string;
	};
};

export type StreamDynamicLayoutSubAgent = {
	type: 'DL_SUB_AGENT';
	message: any;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamReasoningPart = {
	type: 'REASONING_PART';
	message: { content: string; role: 'ASSISTANT'; stepId: string };
	millisOffset: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamTrace = {
	type: 'TRACE';
	message: { content: string; message_template?: string };
	millisOffset: number;
	metadata?: {
		run_id: string;
		request_id: string;
		plugin_name?: string;
		plugin_input?: string;
	};
};

export type StreamResearchTrace = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH'; content: string };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type AnalysingQueryTrace = {
	type: 'TRACE';
	message: { message_template: 'ANALYSING_QUERY'; content: string };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type WritingTrace = {
	type: 'TRACE';
	message: { message_template: 'WRITING'; content: string; user_query?: string | null };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepContent = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH_STEP'; content: string; stepId: string; roundId: number };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepTitle = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_TITLE';
		title: string;
		stepId: string;
		roundId: number;
	};
};

export type StreamResearchPlan = {
	type: 'TRACE';
	message: { message_template: 'RESEARCH_PLAN'; content: string; roundId: number };
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepTitleShort = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_TITLE_SHORT';
		title: string;
		stepId: string;
		roundId: number;
		totalRoundId?: number | null;
		formulated_query?: string | null;
		sources?: Sources | null;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepSource = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_SOURCE';
		sources: Source[];
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStepCompleted = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_STEP_COMPLETE';
		stepId: string;
		roundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamResearchStart = {
	type: 'TRACE';
	message: {
		message_template: 'RESEARCH_START';
		totalRoundId: number;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamSkillStart = {
	type: 'TRACE';
	message: {
		message_template: 'SKILL_START';
		skill_status_metadata: { aliases: string[] };
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamSkillEnd = {
	type: 'TRACE';
	message: {
		message_template: 'SKILL_END';
		skill_status_metadata: { aliases: string[] };
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type FollowUpObject = {
	type: 'SYNTHETIC' | 'LLM_GENERATED';
	text: string;
};

export type StreamFollowUp = {
	type: 'FOLLOW_UP_QUERIES';
	message: {
		follow_ups: FollowUpObject[] | null;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionPart = {
	type: 'ACTION_PART';
	message: MessageAction;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionClassification = {
	type: 'ACTION_CLASSIFICATION';
	message: {
		action_key: string;
		content: string;
	};
	millisOffset: number;
};

export type StreamActionComplete = {
	type: 'ACTION_COMPLETE';
	message: MessageAction;
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamActionPrepare = Omit<StreamActionPart, 'type'> & {
	type: 'ACTION_PREPARE';
};

export type AnswerGenerationType = 'LUMINA' | 'STANDARD';
export type SteamAnsGenerationType = {
	type: 'ANSWER_GENERATION_TYPE';
	message: {
		answer_generation_type: AnswerGenerationType;
	};
	millisOffset: number;
	metadata?: { request_id: string };
};

export type StreamMessage =
	| StreamTrace
	| StreamResearchTrace
	| StreamResearchStepContent
	| StreamResearchStepTitle
	| StreamResearchStepTitleShort
	| StreamResearchStepSource
	| StreamResearchStepCompleted
	| StreamResearchPlan
	| AnalysingQueryTrace
	| WritingTrace
	| StreamAnswerPart
	| StreamReasoningPart
	| StreamResponse
	| StreamError
	| StreamFollowUp
	| StreamActionPart
	| StreamActionClassification
	| ConversationChannelDataMessage
	| StreamReconnectSupportPart
	| SteamAnsGenerationType
	| StreamDynamicLayoutSubAgent
	| StreamHeartbeat;

/*
NLCreate Stream message types
*/
export type NLCreateAgentPayload = {
	latest_user_message: string;
	draft_agent: {
		conversation_starters: string[];
		description: string;
		instructions: string;
		name: string;
	};
	conversation_history: {
		content: string;
		role: 'HUMAN' | 'ASSISTANT';
	}[];
	mime_type: 'text/adf';
};

export type NLCreateAgentStreamDraftAgent = {
	type: 'DRAFT_AGENT';
	message: {
		name: string;
		description: string;
		instructions: string;
		conversation_starters: string[];
	};
	metadata: {
		request_id: string;
	};
};
export type NLCreateAgentStreamAnswerPart = {
	type: 'ANSWER_PART';
	message: {
		content: string;
	};
	metadata: {
		request_id: string;
	};
};

export type NLCreateAgentStreamFinalResponse = {
	type: 'FINAL_RESPONSE';
	message: {
		content: string;
	};
	metadata: {
		request_id: string;
	};
};

export type NLCreateAgentStreamError = {
	type: 'ERROR';
	message: {
		message_template: string;
		content: string;
		status_code: number;
	};
	metadata: {
		request_id: string;
	};
};
export type NLCreateAgentStreamMessage =
	| NLCreateAgentStreamDraftAgent
	| NLCreateAgentStreamAnswerPart
	| NLCreateAgentStreamFinalResponse
	| NLCreateAgentStreamError;

// ************************************************************************************************
// Other types
// ************************************************************************************************

// All responses should be converted into StreamResponse
// - AssistantMessage -> StreamResponse
// - AgentResponse -> StreamResponse
export type RawChatMessage = HumanMessage | AssistantMessage;
export type RawChat = RawChatMessage[];

export type ChatMessage = HumanMessage | StreamResponse;
export type Chat = ChatMessage[];

export type AssistanceServiceProduct =
	| 'confluence'
	| 'jira-software'
	| 'jira-core'
	| 'jsm'
	| 'jpd'
	| 'atlas'
	| 'jira'
	| 'unknown'
	| 'rovo'
	| 'bitbucket'
	| 'trello'
	| 'elevate'
	| 'recruit'
	| 'mercury'
	| 'admin'
	| ''
	| string;

export type AssistanceServiceConfig = {
	baseUrl?: string | undefined;
	headers?: Record<string, string>;
	product: AssistanceServiceProduct;
	credentials?: RequestInit['credentials'];
	experienceId: string;
	minionAlias?: string;
	labelStack?: LabelStack;
};

export type ExternalFile = {
	media_file_id: string;
	file_name: string;
	mime_type: string;
};

export type PostAgentPayload = Pick<
	Agent,
	| 'name'
	| 'description'
	| 'named_id'
	| 'system_prompt_template'
	| 'available_plugins'
	| 'visibility'
	| 'external_files'
	| 'user_defined_conversation_starters'
	| 'knowledge_sources'
	| 'action_config'
	| 'creator'
>;

export type ConversationChannelStatus = 'OPEN' | 'ENDED' | 'EXPIRED' | 'ESCALATED';

export type MessageType = 'DEEP_RESEARCH' | 'WEB_SEARCH';

export type ConversationChannelMessageStatus =
	| 'IN_PROGRESS'
	| 'SUCCESSFUL'
	| 'FAILED'
	| 'CANCELLED';

export type ConversationChannel = {
	id: string;
	experience_id: string;
	owner: string;
	time_created: string;
	status: ConversationChannelStatus;
	name?: string;
	messages?: Message[];
	last_message?: string;
	last_message_type?: MessageType[];
	last_message_timestamp?: string;
	last_agent_message_status?: ConversationChannelMessageStatus;
	assistant_message_id?: string;
};

export interface Dialogues {
	human_message: {
		content: string;
	};
	agent_message: {
		content: string;
	};
}

export type SendMessageDashboardContext =
	| {
			content: string;
	  }
	| undefined;

export type SendMessageEditorContext =
	| {
			document: {
				type: 'text/markdown' | 'text/adf';
				content: string;
			};
			selection: {
				type: 'text/markdown' | 'text/plain';
				content: string;
			};
	  }
	| undefined;

export type SendMessageWhiteboardContext =
	| {
			type: 'image/svg+xml' | 'text/plain';
			content: string;
			contentId?: string;
	  }
	| undefined;

export type SendMessageDatabaseContext =
	| {
			contentId: string;
			csv: string;
			title: string;
			url: string;
	  }
	| undefined;

export type SendMessageChatContext =
	| {
			[contextKey: string]: unknown;
	  }
	| undefined;

export type SendMessageBrowserContext = {
	context:
		| {
				browserUrl: string;
				htmlBody?: string;
				canvasText?: string;
		  }
		| undefined;
};

export type SendMessageWorkflowContext = {
	currentWorkflowDocument?: Record<string, unknown>;
};

export type PermissionId =
	| 'AGENT_CREATE'
	| 'AGENT_READ'
	| 'AGENT_UPDATE'
	| 'AGENT_DEACTIVATE'
	| 'AGENT_DELETE';

export type GetAgentPermissionsPayload = {
	permission_ids: PermissionId[];
};

export type AgentPermissionResponse = {
	permissions: Partial<
		Record<
			PermissionId,
			{
				permitted: boolean;
			}
		>
	>;
};

export type FileUploadResponseItem = {
	id: string;
	conversation_channel_id?: string;
	blob_id?: string;
	name: string;
	size?: number;
	type?: string;
	created_at?: string;
	owner?: string;
};

export type FileMetadataResponseItem = {
	id: string;
	conversation_channel_id: string;
	blob_id: string;
	name?: string;
	size: number;
	type?: string;
	created_at: string;
	owner: string;
};

export type FileUploadResponse = {
	data: FileUploadResponseItem[];
};

export type FileValidationMetadata = {
	originalFilename: string;
	contentType: string;
	size: number;
};

export type FileValidationPayload = {
	fileMetadata: FileValidationMetadata[];
};

export type FileValidationResponse = {
	validationResults: {
		fileMetadata: FileValidationMetadata;
		isSupported: boolean;
		errorCode?:
			| 'INVALID_CONTENT_TYPE'
			| 'FILE_TOO_LARGE'
			| 'FILE_NAME_MISSING'
			| 'UNKNOWN_ERROR'
			| string;
	}[];
};

export type RovoLabsTool = {
	name: string;
	displayName: string;
	description: string;
	/**
	 * This is the JSON schema for the tool's configuration.
	 */
	configurationInJsonSchema?: unknown;
	icon?: string;
	provider: RovoLabsToolProvider;
	integrationKey?: string;
};

export type RovoLabsToolProvider = {
	id: string;
	displayName: string;
};

export type RovoLabsToolPayload = {
	name: string;
	configuration: unknown;
	provider: RovoLabsToolProvider;
};

export type RovoLabsChatStreamPayload = {
	latest_user_message: string | DocNode;
	conversation_id: string;
	workflow_configuration: {
		routerConfig: {
			maxLoop: number;
			selectedTools: RovoLabsToolPayload[];
		};
		toolInvocationConfig: {
			toolInvocationTimeoutMs: number;
		};
		selectedLanguageModelId?: string;
		promptConfig?: {
			systemPromptTemplate?: string;
			includeCurrentUserContext: boolean;
		};
	};
	additional_context?: {
		file_attachment_ids: string[];
	};
	confirmed_tools?: RovoLabsConfirmedTools;
};

export type RovoLabsLanguageModel = {
	modelId: string;
	modelProvider: string;
};

export type RovoLabsDefaultPrompt = {
	defaultSystemPromptTemplate: string;
};

export type RovoLabsConfirmedTool = {
	key: string;
	tool_id?: string;
	tool_source?: string;
	invocation_id: string;
	data: {
		human_message_id: string;
		arguments: unknown;
	};
	resolution?: {
		type?: 'confirmed' | 'cancelled';
	};
};

export type RovoLabsConfirmedTools = {
	message_id: string;
	tools: RovoLabsConfirmedTool[];
};

export type StreamToolSelection = {
	type: 'TOOL_SELECTION';
	message: {
		invocationId: string;
		displayName: string;
		toolInput: unknown;
	};
	millisOffset?: number;
	metadata?: {
		request_id?: string;
	};
};

export type StreamToolInvocation = {
	type: 'TOOL_INVOCATION';
	message: {
		invocationId: string;
		displayName: string;
		toolOutput: unknown;
	};
	millisOffset?: number;
};

/**
 * Interface for the Assistance Service.
 */
export interface AssistanceService {
	sendMessageStream: (payload: {
		conversationId: string;
		message: string | DocNode;
		/**
		 * This can be agentId or agentNamedId, and BE will resolve it correctly
		 * https://atlassian.slack.com/archives/C0677M19GU9/p1725952045906819?thread_ts=1725929736.588339&cid=C0677M19GU9
		 *
		 * As of RAGE-60 the default agent is no longer required to be sent to the backend. So agentNamedId can be undefined.
		 */
		agentNamedId?: string;
		agentId?: string;
		controller?: AbortController;
		storeMessage?: boolean;
		/** Whether to keep the source indexes in the message e.g [^1^], [^2^] */
		citationsEnabled?: boolean;
		minionAliasConfig?: Record<string, unknown>;
		jiraWorkflowContext?: SendMessageWorkflowContext;
		editorContext?: SendMessageEditorContext;
		whiteboardContext?: SendMessageWhiteboardContext;
		databaseContext?: SendMessageDatabaseContext;
		browserContext?: SendMessageBrowserContext;
		additionalContext?: Record<string, unknown>;
		aiFeatureContext?: SendMessageChatContext;
		dashboardContext?: SendMessageDashboardContext;
		fileIds?: string[];
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		webSearchEnabled?: boolean;
		/**
		 * appFilters being undefined means all org knowledge is applied.
		 * appFilters being empty means no org knowledge is applied.
		 * appFilters being non-empty means some org knowledge is applied.
		 */
		appFilters?: string[];
		/**
		 * This when enabled will ensure that the browser_url is always used by the Rovo as context
		 */
		useCurrentBrowsingContext?: boolean;
		optInToDeepResearchV2?: boolean;
		optInThinkDeeperMessages?: boolean;
		/**
		 * Sanitizer for browser context URLs.
		 * Used to check if a URL should be blocked from being used as browser context.
		 * If a URL is blocklisted, an empty URL and empty context will be sent instead.
		 */
		browserContextSanitizer?: {
			isBlocklisted: (url: string) => boolean;
		};
		optInSSEStream?: boolean;
	}) => Promise<FetchStreamReturn<StreamMessage, boolean>>;
	reconnectToMessageStream: (payload: {
		conversationId: string;
		messageId: string;
		lastEventId?: string;
		controller?: AbortController;
	}) => Promise<FetchStreamReturn<StreamMessage, true>>;
	sendMessageConfirmedTools: (payload: {
		conversationId: string;
		agentId?: string;
		controller?: AbortController;
		/**
		 * This is used as an alternative way to execute action/tools
		 * replacing the need to go through `/action` endpoint
		 * https://hello.atlassian.net/wiki/spaces/agents/pages/5974706697/Short-term+Enable+resumption+for+external+MCP
		 */
		confirmed_tools: RovoLabsConfirmedTools;
		optInSSEStream?: boolean;
	}) => Promise<FetchStreamReturn<StreamMessage, boolean>>;
	nlCreateAgentStream: (
		payload: {
			controller?: AbortController;
		} & NLCreateAgentPayload,
	) => Promise<FetchStreamReturn<NLCreateAgentStreamMessage>>;
	getConfig: () => AssistanceServiceConfig;
	getChat: (conversationId: string) => Promise<Chat>;
	deleteChat: (conversationId: string) => Promise<void>;
	getAgents: () => Promise<Agent[]>;
	getAgentDetails: (id: string) => Promise<AgentDetails>;
	getAgentDetailsByIdentityAccountId: (identityAccountId: string) => Promise<AgentDetails>;
	createAgent: (payload: PostAgentPayload) => Promise<Agent>;
	updateAgent: (id: string, payload: PostAgentPayload) => Promise<Agent>;
	deleteAgent: (id: string) => Promise<void>;
	favouriteAgent: (id: string) => Promise<void>;
	unfavouriteAgent: (id: string) => Promise<void>;
	getConversationChannels: () => Promise<ConversationChannel[]>;
	createConversationChannel: (options?: {
		name?: string;
		dialogues?: Dialogues[];
	}) => Promise<ConversationChannel & Required<Pick<ConversationChannel, 'name'>>>;
	updateConversationChannel: (
		id: string,
		payload: Partial<ConversationChannel>,
	) => Promise<ConversationChannel>;
	resolveConversationAction: (
		conversationId: string,
		payload: MessageActionPayload,
	) => Promise<MessageActionResponse>;

	// REST conversation starters, newer API
	generateAgentStarters: (payload: {
		agent_name?: string;
		description?: string;
		system_prompt?: string;
	}) => Promise<ConversationStarterResponse>;

	// REST conversation starters, newer API
	generateContextualStarters: (payload: {
		browser_url: string;
	}) => Promise<ConversationStarterResponse>;

	// REST dynamic conversation starters (new endpoint. /convo-starters)
	getDynamicConvoStartersNew: (
		payload: DynamicConversationStarterRequestNew,
	) => Promise<DynamicConversationStarterResponseNew>;

	getAgentActions: () => Promise<AgentAction[]>;
	getAgentKnowledgeConfiguration: () => Promise<AgentKnowledgeConfigurationResponse>;
	getAgentPermissions: (
		agentId: string,
		payload: GetAgentPermissionsPayload,
	) => Promise<AgentPermissionResponse>;
	getRecommendedAgents: (payload: { limit: number; query: string; pageUrl?: string }) => Promise<{
		agents: Agent[];
		metadata: { recommendationKey: string };
	}>;
	getConversationDebugLogs: (conversationId: string) => Promise<Chat>;
	uploadFiles: (payload: { files: File[] }) => Promise<FileUploadResponse>;
	validateFiles: (payload: FileValidationPayload) => Promise<FileValidationResponse>;
	rovoLabsSendMessage: (
		payload: RovoLabsChatStreamPayload,
	) => Promise<FetchStreamReturn<StreamMessage | StreamToolSelection | StreamToolInvocation>>;
	rovoLabsGetConversations: () => Promise<{ items: ConversationChannel[] }>;
	rovoLabsGetTools: () => Promise<{ tools: RovoLabsTool[] }>;
	rovoLabsGetLanguageModels: () => Promise<{ languageModels: RovoLabsLanguageModel[] }>;
	rovoLabsGetPrompts: () => Promise<RovoLabsDefaultPrompt>;
	getWebSearchConfiguration: () => Promise<WebSearchConfigurationResponse>;
	getPrompts: () => Promise<GetAllPromptsResponse>;
	getPrompt: (promptId: string) => Promise<GetPromptResponse>;
	createPrompt: (payload: CreatePromptRequest) => Promise<GetPromptResponse>;
	deletePrompt: (shareId: string) => Promise<void>;
	getGeneratedContent: (payload?: {
		actions?: string[];
		statuses?: string[];
		limit?: number;
	}) => Promise<ConversationContent[]>;
}

// ************************************************************************************************
// Type Guards
// ************************************************************************************************

export const isChatMessage = (message: any): message is ChatMessage => {
	return isHumanMessage(message) || isAssistantMessage(message) || isStreamResponse(message);
};

export const isHumanMessage = (message: any): message is HumanMessage =>
	message instanceof Object && message.role === 'HUMAN';

export const isAssistantMessage = (message: any): message is AssistantMessage =>
	message instanceof Object && message.role === 'ASSISTANT';

export const isAgentResponse = (message: any): message is AgentResponse =>
	message instanceof Object && isAssistantMessage(message.message);

export const isInProgressStreamResponse = (message: any): message is StreamResponse =>
	isStreamResponse(message) && message.message?.message?.async_status === 'IN_PROGRESS';

export const isStreamResponse = (message: any): message is StreamResponse =>
	message instanceof Object && message.type === 'FINAL_RESPONSE';

export const isStreamError = (message: any): message is StreamError =>
	message instanceof Object && message.type === 'ERROR';

export const isStreamAUPError = (message: any): message is StreamError =>
	isStreamError(message) && message.message?.message_template === ACCEPTABLE_USE_VIOLATIONS_ERROR;

export const isStreamAnswerPart = (message: any): message is StreamAnswerPart =>
	message instanceof Object && message.type === 'ANSWER_PART';

export const isStreamReasoningPart = (message: any): message is StreamReasoningPart =>
	message instanceof Object && message.type === 'REASONING_PART';

export const isStreamActionPart = (message: any): message is StreamActionPart =>
	message instanceof Object && message.type === 'ACTION_PART';

export const isStreamActionClassification = (message: any): message is StreamActionClassification =>
	message instanceof Object && message.type === 'ACTION_CLASSIFICATION';

export const isStreamActionPrepare = (message: any): message is StreamActionPrepare =>
	message instanceof Object && message.type === 'ACTION_PREPARE';

export const isStreamActionComplete = (message: any): message is StreamActionComplete =>
	message instanceof Object && message.type === 'ACTION_COMPLETE';

export const isStreamResearchStart = (message: any): message is StreamResearchStart =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_START';

export const isStreamResearchBeginningTrace = (
	message: any,
): message is StreamResearchStepContent =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH';

export const isStreamResearchStepContent = (message: any): message is StreamResearchStepContent =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP';

export const isStreamResearchStepTitle = (message: any): message is StreamResearchStepTitle =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_TITLE';

export const isStreamResearchPlan = (message: any): message is StreamResearchPlan =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_PLAN';

export const isStreamResearchStepTitleShort = (
	message: any,
): message is StreamResearchStepTitleShort =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_TITLE_SHORT';

export const isStreamSkillStart = (message: any): message is StreamSkillStart =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'SKILL_START';

export const isStreamSkillEnd = (message: any): message is StreamSkillEnd =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'SKILL_END';

export const isStreamResearchStepSource = (message: any): message is StreamResearchStepSource =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_SOURCE';

export const isStreamResearchStepCompleted = (
	message: any,
): message is StreamResearchStepCompleted =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	message.message.message_template === 'RESEARCH_STEP_COMPLETE';

export const isStreamReconnectSupportPart = (message: any): message is StreamReconnectSupportPart =>
	message instanceof Object && message.type === 'RECONNECT_SUPPORTED';
export const isStreamAnswerGenerationType = (message: any): message is SteamAnsGenerationType =>
	message instanceof Object && message.type === 'ANSWER_GENERATION_TYPE';

export const isStreamDynamicLayoutSubAgent = (
	message: any,
): message is StreamDynamicLayoutSubAgent =>
	message instanceof Object && message.type === 'DL_SUB_AGENT';

export const isStreamResearchPayload = (
	message: any,
): message is
	| StreamResearchTrace
	| StreamResearchStepContent
	| StreamResearchStepTitle
	| StreamResearchStepTitleShort
	| StreamResearchStepSource
	| StreamResearchStepCompleted =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	(message.message.message_template === 'RESEARCH' ||
		message.message.message_template === 'RESEARCH_STEP' ||
		message.message.message_template === 'RESEARCH_STEP_TITLE' ||
		message.message.message_template === 'RESEARCH_STEP_TITLE_SHORT' ||
		message.message.message_template === 'RESEARCH_STEP_SOURCE' ||
		message.message.message_template === 'RESEARCH_STEP_COMPLETE');

export const isStreamSkill = (message: any): boolean =>
	message instanceof Object &&
	message.type === 'TRACE' &&
	(isStreamSkillStart(message) || isStreamSkillEnd(message));

export const isStreamTrace = (message: any): message is StreamTrace => {
	// TRACE and research steps are sharing the same type, but we want to separate them
	if (isStreamResearchPayload(message)) {
		return false;
	}

	if (isStreamResearchPlan(message)) {
		return false;
	}

	if (isStreamSkill(message)) {
		return false;
	}

	return message instanceof Object && message.type === 'TRACE';
};

export const isFollowUpMessage = (message: unknown): message is StreamFollowUp =>
	message !== undefined &&
	message !== null &&
	typeof message === 'object' &&
	'type' in message &&
	message.type === 'FOLLOW_UP_QUERIES';

export const isStreamHeartbeat = (message: any): message is StreamHeartbeat =>
	message instanceof Object && message.type === 'HEART_BEAT';

export const isActionExecutedSuccessfully = (message: any): boolean =>
	isAssistantMessage(message) &&
	(message?.message_metadata?.is_action_execution ?? false) &&
	message?.message_metadata?.action_execution_result === 'SUCCESS';

export const isActionResolutionCancelled = (
	payloadContext: MessageActionPayloadContext,
): boolean => {
	return payloadContext?.resolution?.type === 'cancel';
};

export const isChannelNotFoundError = (error: unknown): boolean =>
	isApiError(error) && error.errorAttributes?.errorCode === CONVERSATION_CHANNEL_NOT_FOUND_ERROR;

export const isStreamActionError = (msg: any): msg is StreamActionError =>
	msg && msg.type === 'ACTION_ERROR' && msg.message && msg.message.status === 'ERRORED';

export const isRateLimitedError = (error: unknown): boolean =>
	isApiError(error) && error.errorAttributes.statusCode === 429;

export const isHipaaError = (error: unknown): boolean =>
	isApiError(error) &&
	!!error.errorAttributes.errorCode &&
	error.errorAttributes.errorCode === ASSISTANCE_SERVICE_ERROR_CODES.HAS_HIPAA_ENABLED;

export const isAUPViolationError = (error: any): boolean =>
	error instanceof Object &&
	error?.detail?.type === 'ERROR' &&
	!!error.errorAttributes.errorCode &&
	error.errorAttributes.errorCode === ACCEPTABLE_USE_VIOLATIONS_ERROR;

export const isConversationChannelDataMessage = (
	message: any,
): message is ConversationChannelDataMessage =>
	message instanceof Object && message.type === 'CONVERSATION_CHANNEL_DATA';

export function isConfirmAction(payloadContext: MessageActionPayloadContext): boolean {
	return payloadContext?.resolution?.type === 'confirm';
}

export type Source = {
	ari: string;
	title: string;
	type: string;
	url: string;
	lastModified: string;
	// @deprecated this is no longer being returned by BE, keeping it because some legacy unified-help code is still using it
	id?: number;
	product?: string;
};

export type Sources = Array<Source>;

export type Metadata = {
	deep_research?: boolean;
	// Unified Help Only
	bm25Variant?: string;
	confidenceScore?: number;
	isFallbackMessage?: boolean;
	originalQuery?: string;
	semanticSearchLocationVariant?: string;
	semanticSearchVariant?: string;
	action_execution_result?: string;
	is_action_execution?: boolean;
	invocation_id?: string;
	remote_entity_id?: string;
	requestId?: string;
	actionOutput?: {
		issueId?: string;
		url?: string;
	};
};

export type Appendix = {
	type: 'requestForm' | 'helpDesk';
	content: string;
	appendix_sources?: Sources;
};

export type Appendices = Array<Appendix>;

export type Message = {
	role: string;
	content: string | DocNode;
	id: string;
	time_created: string;
	user_ari: string;
	plugin_invocations: PluginInvocationMessage[];
	sources?: Sources;
	message_metadata?: Metadata;
	author?: {
		actor_type: string;
		ari: string | null;
		id: string;
		name: string;
		named_id?: string;
	};
	author_id?: string;
	conversation_channel_id?: string;
	appendices?: Appendices;
	actions?: MessageAction[];
	async_status?: 'IN_PROGRESS' | 'SUCCESSFUL' | 'FAILED' | 'CANCELLED';
	content_mime_type?: 'text/markdown';
	experience_id?: string;
	cloud_id?: string;
	product_activation_id?: string;
	jira_activation_id?: string;
	user_org_id?: string;
	audio?: null;
	conversation_outcome?: null;
	orchestration_graphs?: null | unknown[];
	minion_outputs?:
		| null
		| {
				minion_id: string;
				minion_input: string;
				minion_output: string;
				minion_execution_duration: number;
				minion_extra_info: string | null;
				directly_invoked: boolean;
		  }[];
	scenario?: {
		id: string;
		name: string;
		actions: string[];
	} | null;
};

export type StreamActionError = {
	type: string;
	message: {
		key: string;
		invocationId: string;
		actionId: string | null;
		data: { message: string };
		status: string;
	};
	metadata?: any;
	millisOffset?: number;
};

export type AgentPlugins = {
	'Activity-Plugin': object;
	'Arithmetic-Plugin': object;
	'Content-Read-Plugin': object;
	'Jira-Issue-Create-Plugin': object;
	'Jira-JQL-Plugin': object;
	'Jira-Software-Plugin': object;
	'Next-Best-Task-Plugin': object;
	'Open-Toolchain-Plugin': object;
	'Profile-Plugin': object;
	'Search-QA-Plugin': {
		space_filter?: string[];
	};
	'Unified-Help-Plugin': object;
};

/* TODO: This will evolve as we enrich support for plugins, for the moment
we simply expect to make a direct replacement for `config.space_filters`
with `config.available_plugins['Your-Plugin'].space_filters`. Right now we only support
configuring one plugin, which is the Search-QA-Plugin, which can take the
space_filters configuration, per what our AgentForm expects

Related BE PR: https://bitbucket.org/atlassian/assistance-service/pull-requests/461/aim-367-allow-agents-to-use-plugin-config
*/
export type AgentAvailablePlugins = {
	[k in keyof AgentPlugins]?: AgentPlugins[k];
};

export type AgentKnowledgeSourceBase<T extends string, U = undefined> = {
	enabled: boolean;
	source: T;
	configuration: U;
};

export type ConfluenceKnowledgeSource = AgentKnowledgeSourceBase<
	'confluence',
	{
		space_filter: string[];
		parent_filter: string[];
	}
>;

export type JiraKnowledgeSource = AgentKnowledgeSourceBase<
	'jira',
	{
		project_filter: string[];
	}
>;

export type JsmKnowledgeSource = AgentKnowledgeSourceBase<
	'jsm',
	{
		project_filter: string[];
	}
>;

export type AtlasKnowledgeSource = AgentKnowledgeSourceBase<'atlas'>;
export type GoogleDriveKnowledgeSource = AgentKnowledgeSourceBase<'google_drive'>;
export type MicrosoftSharepointKnowledgeSource = AgentKnowledgeSourceBase<'microsoft_sharepoint'>;
export type SlackKnowledgeSource = AgentKnowledgeSourceBase<'slack'>;
export type LoomKnowledgeSource = AgentKnowledgeSourceBase<'loom'>;
export type OtherKnowledgeSource = AgentKnowledgeSourceBase<string>;

export type AgentKnowledgeSource =
	| ConfluenceKnowledgeSource
	| JiraKnowledgeSource
	| JsmKnowledgeSource
	| AtlasKnowledgeSource
	| GoogleDriveKnowledgeSource
	| MicrosoftSharepointKnowledgeSource
	| SlackKnowledgeSource
	| LoomKnowledgeSource
	| OtherKnowledgeSource;

export type AgentKnowledgeConfiguration = {
	// Known sources
	source:
		| 'confluence'
		| 'jira'
		| 'jsm'
		| 'atlas'
		| 'microsoft_sharepoint'
		| 'google_drive'
		| 'slack'
		| 'loom'
		| string;
	iconUrl: string;
	friendlyName: string;
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
};

export type MappedAgentKnowledgeConfiguration = AgentKnowledgeConfiguration & {
	checkboxFieldName: string;
	actionSubjectId: string;
	checkboxLabel: string;
};

export type AgentKnowledgeConfigurationResponse = {
	knowledgeSources: AgentKnowledgeConfiguration[];
};

export type AgentScenarioKnowledgeSource = {
	enabled?: boolean;
	source: string;
	filters?: {
		parent_filter?: string[];
		space_filter?: string[];
		project_filter?: string[];
	};
};

export type AgentScenarioAction = {
	id: string;
	name?: string;
	displayName?: string;
	description?: string;
	tags?: string[];
	iconUrl?: string;
};

export type AgentScenario = {
	id: string;
	name: string;
	invocationDescription?: string;
	instructions?: string;
	knowledgeSources?: {
		enabled?: boolean;
		sources?: AgentScenarioKnowledgeSource[];
	};
	actions?: AgentScenarioAction[];
};

export type Agent = {
	id: string;
	identity_account_id?: string | null;
	named_id: string;
	name: string;
	description: string | null;
	system_prompt_template?: string | null;
	behaviour?: string | null;
	creator_type: 'SYSTEM' | 'CUSTOMER' | 'THIRD_PARTY' | 'FORGE' | 'OOTB';
	creator?: string | null;
	available_plugins: AgentAvailablePlugins;
	visibility?: 'PUBLIC' | 'PRIVATE' | null;
	is_default: boolean;
	external_files?: ExternalFile[] | null;
	actor_type: 'AGENT';
	creator_cloud_id?: string | null;
	follow_up_prompt_template?: string | null;
	plugin_routing_type?: 'DEFAULT' | 'SKIP' | null;
	user_defined_conversation_starters: string[] | null;
	favourite: boolean;
	deactivated: boolean;
	deactivatedAt?: string;
	knowledge_sources?: {
		enabled: boolean;
		sources: AgentKnowledgeSource[];
	} | null;
	action_config?: AgentAction[] | null;
	external_config_reference?: string;
	icon?: string | null;
	authoring_team?: {
		id: string;
		profile_url: string;
		display_name: string;
		avatar_url?: string;
	} | null;
	is_verified?: boolean | null;
	agentAri?: string | null;
};

export type AgentDetails = Agent & {
	favourite_count: number;
};

export type ConversationChannelDataMessage = {
	type: 'CONVERSATION_CHANNEL_DATA';
	assistant_message_id: string;
} & Pick<ConversationChannel, 'name' | 'time_created' | 'last_message_timestamp'>;

export type ConversationStarterResponse = {
	conversation_starters: { text: string }[];
};

export type DynamicConversationStarterRequestNew =
	AiFeatureRequestEnvelopeRovoConvoStarterRecommendationRequestNew;

export type DynamicConversationStarterResponseNew =
	| AIFeatureResponseEnvelopeRovoConvoStarterRecommendationResponseNew
	| undefined;

export type FetchStreamReturn<T, isSSEFormatted extends boolean = false> = {
	responseData: {
		traceId?: string;
	};
	generator: isSSEFormatted extends true
		? AsyncGenerator<ParsedSSEMessage<T>> // SSE: always ParsedSSEMessage<T>
		: AsyncGenerator<T>; // Non-SSE: clean type, no check needed
};
